import { ReactNode } from 'react';
import { FormProvider } from 'react-hook-form';

import type { FormProps } from './types';
import type {
  FormCheckboxProps,
  FormDatePickerProps,
  FormImageTileProps,
  FormImageUploadProps,
  FormInputProps,
  FormMultiSelectProps,
  FormPhoneInputProps,
  FormRadioGroupProps,
  FormSelectProps,
  FormSlotPickerProps,
  FormTextareaProps,
  FormTextEditorProps,
} from '@/components/FormFields';
import type { SyntheticEvent } from 'react';
import type { FieldValues } from 'react-hook-form';

import {
  FormCheckbox,
  FormDatePicker,
  FormImageTile,
  FormImageUpload,
  FormInput,
  FormMultiSelect,
  FormPhoneInput,
  FormRadioGroup,
  FormSelect,
  FormSlotPicker,
  FormTextarea,
  FormTextEditor,
} from '@/components/FormFields';
import { FieldGroup } from '@/components/ui/field';

/**
 * Main Form component
 * Handles form logic (FormProvider, form element, FieldGroup)
 * Should be wrapped with FormWrapper for UI (Card, header, buttons)
 * Exports all field components as static properties (Form.Input, Form.Textarea, etc.)
 */
const Form = <
  TFieldValues extends FieldValues = FieldValues,
  TTransformedValues extends FieldValues = TFieldValues,
>(
  props: FormProps<TFieldValues, TTransformedValues>,
) => {
  const { form, id, onSubmit, children, className, fieldGroupClassName } =
    props;

  const onFormSubmit = onSubmit
    ? (event: SyntheticEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        onSubmit();
      }
    : undefined;

  return (
    <FormProvider {...form}>
      <form
        noValidate
        id={id}
        onSubmit={onFormSubmit}
        className={className}
      >
        <FieldGroup className={fieldGroupClassName}>{children}</FieldGroup>
      </form>
    </FormProvider>
  );
};

// Export field components as static properties
Form.Input = FormInput;
Form.Textarea = FormTextarea;
Form.DatePicker = FormDatePicker;
Form.ImageUpload = FormImageUpload;
Form.ImageTile = FormImageTile;
Form.Checkbox = FormCheckbox;
Form.Select = FormSelect;
Form.RadioGroup = FormRadioGroup;
Form.MultiSelect = FormMultiSelect;
Form.TextEditor = FormTextEditor;
Form.PhoneInput = FormPhoneInput;
Form.SlotPicker = FormSlotPicker;

type FormFields<TFieldValues extends FieldValues> = {
  Input: (props: FormInputProps<TFieldValues>) => ReactNode;
  Textarea: (props: FormTextareaProps<TFieldValues>) => ReactNode;
  DatePicker: (props: FormDatePickerProps<TFieldValues>) => ReactNode;
  ImageUpload: (props: FormImageUploadProps<TFieldValues>) => ReactNode;
  ImageTile: (props: FormImageTileProps<TFieldValues>) => ReactNode;
  Checkbox: (props: FormCheckboxProps<TFieldValues>) => ReactNode;
  Select: (props: FormSelectProps<TFieldValues>) => ReactNode;
  RadioGroup: (props: FormRadioGroupProps<TFieldValues>) => ReactNode;
  MultiSelect: (props: FormMultiSelectProps<TFieldValues>) => ReactNode;
  TextEditor: (props: FormTextEditorProps<TFieldValues>) => ReactNode;
  PhoneInput: (props: FormPhoneInputProps<TFieldValues>) => ReactNode;
  SlotPicker: (props: FormSlotPickerProps<TFieldValues>) => ReactNode;
};

const typedFields = <
  TFieldValues extends FieldValues,
>(): FormFields<TFieldValues> => ({
  Input: FormInput,
  Textarea: FormTextarea,
  DatePicker: FormDatePicker,
  ImageUpload: FormImageUpload,
  ImageTile: FormImageTile,
  Checkbox: FormCheckbox,
  Select: FormSelect,
  RadioGroup: FormRadioGroup,
  MultiSelect: FormMultiSelect,
  TextEditor: FormTextEditor,
  PhoneInput: FormPhoneInput,
  SlotPicker: FormSlotPicker,
});

export { Form, typedFields };
export type { FormFields };
export type { FormProps } from './types';
